import BtnFilled from '../../../components/BtnFilled';
import AlertAccent from '../../../components/AlertAccent';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntegrationsStore } from '../../../stores/use-integrations-store';
import { FormProvider, useForm } from 'react-hook-form';
import DynamicIntegrationForm from './DynamicIntegrationForm';
import { addIntegrationApi } from '../../../api/api';
import { toast } from 'react-toastify';
import ControlledInput from '../../../components/ControlledInput';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useUserStore } from '../../../stores';
import { ControlledCheckbox } from '../../../components/Checkbox';
import TelegramIntegration from './TelegramIntegration';
import useThrottle from '../../../utils/use-throttle';
import { ERROR_TEXT } from '../../../constants';
import ControlledDropdown from '../../../components/ControlledDropdown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { GisZhkkhSubscribeNotice, isGisZhkkh, subscribeGisZhkkh } from '../gisZhkkh';

dayjs.extend(utc);

const AddIntegrationForm = ({ onClose, device }) => {
  const { fetchIntegrations, integrationsType, templates } = useIntegrationsStore();
  const methods = useForm({ mode: 'onTouched', shouldUnregister: true });
  const { user } = useUserStore();
  const [submittingGis, setSubmittingGis] = useState(false);

  const integrationKey = methods.watch('integrationKey');
  const templateId = methods.watch('template_id');
  const isTemplateType = integrationKey === 'email_template';
  const isManualIntegration = integrationKey === 'integrations.manual';
  const isTelegramIntegration = integrationKey === 'telegram_bot_integration';
  const isGis = isGisZhkkh(integrationKey);
  const acceptAgreement = methods.watch('acceptAgreement');

  // установка email при выборе templateId
  useEffect(() => {
    if (templateId) {
      const email = templates.find((item) => item.template_id === templateId)?.email;
      if (email) {
        methods.setValue('email', email);
      }
    }
  }, [templateId, templates, methods]);

  const selectedConfiguration = useMemo(() => {
    const selectIntegration = integrationsType.find((item) => item.key === integrationKey);
    return selectIntegration?.config?.schema;
  }, [integrationKey, integrationsType]);

  const handleSubmit = useThrottle(async (data) => {
    if (isGisZhkkh(integrationKey)) {
      if (submittingGis) return;
      setSubmittingGis(true);
      try {
        await subscribeGisZhkkh({
          deviceId: device.device_id,
          config: data,
          redirect: true,
        });
      } catch (err) {
        toast.error(err?.message || 'Не удалось оформить подписку', { hideProgressBar: true });
        setSubmittingGis(false);
      }
      return;
    }

    // преобразование скрытых системных полей:
    const systemFields = {
      device_id: device.device_id,
      is_active: true,
      enabled: true,
      organization_id: user?.organization_id,
      created_at: dayjs.utc().toISOString(),
    };
    Object.keys(systemFields).forEach((key) => {
      if (key in data) {
        data[key] = systemFields[key];
      }
    });
    if (data.date) {
      data.date = dayjs().date(Number(data.date)).format('YYYY-MM-DD');
    }

    await addIntegrationApi(
      data,
      integrationKey,
      device.device_id,
      async function () {
        await fetchIntegrations();
        onClose();
        toast.success('Интеграция успешно добавлена!');
      },
      function (err) {
        toast.error(err.error, { hideProgressBar: true });
      }
    );
  }, 1000);

  // фильтр для интеграций счетчика и датчика:
  const isMail = (item) => item.key === 'notifier.mail';
  const isSensor = device?.device_type === 'Датчик';
  const filterIntegrationsType = (integrationsType || []).filter((item) => (isSensor ? isMail(item) : !isMail(item)));

  return (
    <div className="bg-white shadow-3xl rounded-3xl">
      <div className={'flex items-center gap-1.5 px-2 pb-3 text-gray-600 text-sm'}>
        <InfoOutlinedIcon fontSize="small" />
        <span>Интеграция - это когда показания передаются в вашу управляющую компанию автоматически</span>
      </div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          {filterIntegrationsType.length !== 0 ? (
            <>
              <ControlledDropdown
                name={'integrationKey'}
                rules={{
                  required: { value: true, message: ERROR_TEXT.REQUIRED },
                }}
                className="w-full mb-4"
                label={'Выберите тип интеграции'}
                defaultValue={''}
                options={filterIntegrationsType}
                placeholder={'Выберите тип интеграции'}
                id="ints"
                valkey="key"
                optkey="name"
                optkey2={'description'}
              />
              {isGis && <GisZhkkhSubscribeNotice />}
              {isTemplateType && (
                <>
                  <ControlledDropdown
                    name={'template_id'}
                    className="w-full mb-4"
                    label={'Выберите УК'}
                    options={templates}
                    type={'number'}
                    defaultValue={templates?.[0]?.template_id || 1}
                    id="templates"
                    valkey="template_id"
                    optkey="company"
                  />
                  <ControlledInput defaultValue={templates?.[0]?.email} disabled={true} classContainer={'mb-4'} name={'email'} label={'Email УК'} />
                </>
              )}
              {/*интеграции:*/}
              {selectedConfiguration && <DynamicIntegrationForm integrationType={integrationKey} configuration={selectedConfiguration} />}
              {isManualIntegration && (
                <ControlledCheckbox
                  containerClassName={'mb-3'}
                  control={methods.control}
                  name={'acceptAgreement'}
                  label={
                    <>
                      Я соглашаюсь с условием{' '}
                      <a rel="noreferrer" target="_blank" href={'/oferta.pdf'} className="inline text-orange-500 underline cursor-pointer">
                        публичной оферты
                      </a>
                    </>
                  }
                />
              )}
              {isTelegramIntegration && <TelegramIntegration />}

              <BtnFilled
                disabled={submittingGis || (isManualIntegration && !acceptAgreement)}
                className="w-full mb-0"
                title={isGis ? (submittingGis ? 'Оформление…' : 'Оформить подписку') : 'Добавить'}
                type={'submit'}
              />
            </>
          ) : (
            <AlertAccent title="Для данного устройства нет доступных интеграций" text="Выберите другое устройство" showAlert={true}></AlertAccent>
          )}
        </form>
      </FormProvider>
    </div>
  );
};
export default AddIntegrationForm;
